import { CouponItem } from '@/components/Product/AddToCart';
import { commonAction } from '../types/commonActions';
import { createContext } from './createContext';
import { useContext } from 'react';

const ACTIONS = {
  UPDATE_IN_CART_ITEMS: 'UPDATE_IN_CART_ITEMS',
  UPDATE_COUPON: 'UPDATE_COUPON',
  UPDATE_PREXO: 'UPDATE_PREXO',
  UPDATE_INVENTORY: 'UPDATE_INVENTORY',
  UPDATE_CURRENT_PRODUCT: 'UPDATE_CURRENT_PRODUCT',
  REMOVE_ALL_COUPONS: 'REMOVE_ALL_COUPONS',
  REMOVE_OTHER_COUPONS: 'REMOVE_OTHER_COUPONS',
};

type ProductInventory = {
  availability: boolean;
  deliveryEstimate: null | string;
  inventory: number;
  inventoryStatus: string;
  productId: string;
  warehouseCode: string;
};

type ProductPrexo = {};

export type Product = {
  appliedCoupon?: CouponItem;
  inventory: null | ProductInventory;
  prexo: null | ProductPrexo;
};

type ProductData = {
  [key: string]: Product;
};

export type ECOMProductDataType = {
  inCartItems: string[];
  productData: ProductData;
  currentProduct: any;
};

export type ECOMProductDispatchType = {
  updateInCartItems: Function;
  updateAppliedCoupon: Function;
  updateInventory: Function;
  updatePrexo: Function;
  updateCurrentProduct: Function;
  removeAllCoupons: Function;
  removeOtherCoupons: Function;
};

export function ecomProductReducer(state: ECOMProductDataType, action: commonAction) {
  const { type, payload } = action;
  const { materialId } = payload;
  switch (type) {
    case ACTIONS.UPDATE_IN_CART_ITEMS:
      return { ...state, inCartItems: payload };
    case ACTIONS.UPDATE_COUPON:
      return {
        ...state,
        productData: {
          ...state.productData,
          [materialId]: {
            ...state.productData[materialId],
            appliedCoupon: payload.appliedCoupon,
          },
        },
      };
    case ACTIONS.UPDATE_INVENTORY:
      return {
        ...state,
        productData: {
          ...state.productData,
          [materialId]: {
            ...state.productData[materialId],
            inventory: payload.inventory,
          },
        },
      };
    case ACTIONS.UPDATE_PREXO:
      return {
        ...state,
        productData: {
          ...state.productData,
          [materialId]: {
            ...state.productData[materialId],
            prexo: payload.prexo,
          },
        },
      };
    case ACTIONS.UPDATE_CURRENT_PRODUCT:
      return {
        ...state,
        currentProduct: payload,
      };
    case ACTIONS.REMOVE_ALL_COUPONS:
      const updatedProductData = Object.keys(state.productData).reduce((acc, key) => {
        acc[key] = {
          ...state.productData[key],
          appliedCoupon: undefined,
        };
        return acc;
      }, {} as ProductData);

      return {
        ...state,
        productData: updatedProductData,
      };
    case ACTIONS.REMOVE_OTHER_COUPONS:
      const modifiedProductData = Object.keys(state.productData).reduce((acc, key) => {
        acc[key] = {
          ...state.productData[key],
          appliedCoupon: key === materialId ? state.productData[key].appliedCoupon : undefined,
        };
        return acc;
      }, {} as ProductData);

      return {
        ...state,
        productData: modifiedProductData,
      };
    default:
      return state;
  }
}

const INITIAL_STATE: ECOMProductDataType = {
  inCartItems: [],
  productData: {},
  currentProduct: {},
};

const updateInCartItems = (dispatch: any) => (payload: string[]) =>
  dispatch({
    type: ACTIONS.UPDATE_IN_CART_ITEMS,
    payload,
  });

const updateAppliedCoupon = (dispatch: any) => (payload: any) =>
  dispatch({
    type: ACTIONS.UPDATE_COUPON,
    payload,
  });

const updatePrexo = (dispatch: any) => (payload: any) =>
  dispatch({
    type: ACTIONS.UPDATE_PREXO,
    payload,
  });

const updateInventory = (dispatch: any) => (payload: any) =>
  dispatch({
    type: ACTIONS.UPDATE_INVENTORY,
    payload,
  });

const updateCurrentProduct = (dispatch: any) => (payload: any) =>
  dispatch({
    type: ACTIONS.UPDATE_CURRENT_PRODUCT,
    payload,
  });

const removeAllCoupons = (dispatch: any) => () =>
  dispatch({
    type: ACTIONS.REMOVE_ALL_COUPONS,
    payload: {},
  });

const removeOtherCoupons = (dispatch: any) => (payload: any) =>
  dispatch({
    type: ACTIONS.REMOVE_OTHER_COUPONS,
    payload,
  });

const { Context: EcomProductContext, Provider: EcomProductProvider } = createContext<
  ECOMProductDataType,
  ECOMProductDispatchType
>(
  ecomProductReducer,
  {
    updateInCartItems,
    updateAppliedCoupon,
    updateInventory,
    updatePrexo,
    updateCurrentProduct,
    removeAllCoupons,
    removeOtherCoupons,
  },
  INITIAL_STATE,
);

EcomProductContext.displayName = 'ECOMProduct';

export const useEcomProductContext = () => useContext(EcomProductContext);

export { EcomProductContext, EcomProductProvider };
